import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Wallet, ArrowDownToLine, Clock, CheckCircle, AlertCircle,
  Building2, CreditCard, IndianRupee, History, Plus, X
} from 'lucide-react';
import ObserverNav from '../components/ObserverNav';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0
  }).format(amount);
};

const ObserverWithdrawal = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [balance, setBalance] = useState(null);
  const [history, setHistory] = useState([]);
  const [bankDetails, setBankDetails] = useState(null);
  const [showWithdrawModal, setShowWithdrawModal] = useState(false);
  const [showBankModal, setShowBankModal] = useState(false);
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [withdrawNotes, setWithdrawNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  
  // Bank form state
  const [bankForm, setBankForm] = useState({
    account_holder: '',
    account_number: '',
    ifsc_code: '',
    bank_name: '',
    branch: ''
  });

  useEffect(() => {
    const token = localStorage.getItem('observer_token');
    if (!token) {
      navigate('/observer/login');
      return;
    }
    loadData();
  }, [navigate]);

  const loadData = async () => {
    try {
      const token = localStorage.getItem('observer_token');
      
      const [balanceRes, historyRes, bankRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/observer/withdrawal/balance`, { params: { token } }),
        axios.get(`${BACKEND_URL}/api/observer/withdrawal/history`, { params: { token } }),
        axios.get(`${BACKEND_URL}/api/observer/bank-details`, { params: { token } })
      ]);
      
      setBalance(balanceRes.data);
      setHistory(historyRes.data.withdrawals || []);
      setBankDetails(bankRes.data);
    } catch (error) {
      console.error('Error loading data:', error);
      if (error.response?.status === 401) {
        localStorage.removeItem('observer_token');
        navigate('/observer/login');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleWithdraw = async () => {
    const amount = parseFloat(withdrawAmount);
    if (isNaN(amount) || amount < 500) {
      alert('Minimum withdrawal amount is ₹500');
      return;
    }
    if (amount > (balance?.available_balance || 0)) {
      alert('Amount exceeds available balance');
      return;
    }
    if (!bankDetails?.has_bank_details) {
      alert('Please add bank details first');
      setShowWithdrawModal(false);
      setShowBankModal(true);
      return;
    }

    setSubmitting(true);
    try {
      const token = localStorage.getItem('observer_token');
      await axios.post(`${BACKEND_URL}/api/observer/withdrawal/request`, null, {
        params: { token, amount, payment_method: 'bank_transfer', notes: withdrawNotes }
      });
      
      alert('Withdrawal request submitted successfully!');
      setShowWithdrawModal(false);
      setWithdrawAmount('');
      setWithdrawNotes('');
      loadData();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to submit withdrawal request');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSaveBankDetails = async () => {
    if (!bankForm.account_holder || !bankForm.account_number || !bankForm.ifsc_code || !bankForm.bank_name) {
      alert('Please fill all required fields');
      return;
    }
    if (bankForm.ifsc_code.length !== 11) {
      alert('IFSC code must be 11 characters');
      return;
    }

    setSubmitting(true);
    try {
      const token = localStorage.getItem('observer_token');
      await axios.post(`${BACKEND_URL}/api/observer/bank-details`, null, {
        params: { token, ...bankForm }
      });
      
      alert('Bank details saved successfully!');
      setShowBankModal(false);
      loadData();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to save bank details');
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-800',
      processing: 'bg-blue-100 text-blue-800',
      approved: 'bg-green-100 text-green-800',
      completed: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800'
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status] || 'bg-gray-100'}`}>
        {status?.charAt(0).toUpperCase() + status?.slice(1)}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <ObserverNav />
        <div className="pt-20 flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="observer-withdrawal-page">
      <ObserverNav />
      
      <div className="pt-20 pb-8 px-4">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-2xl font-bold text-gray-900 mb-6">Withdrawal</h1>

          {/* Balance Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <Card className="bg-gradient-to-br from-emerald-500 to-teal-600 text-white">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-emerald-100 text-sm">Available Balance</p>
                    <p className="text-2xl font-bold">{formatCurrency(balance?.available_balance || 0)}</p>
                  </div>
                  <Wallet className="w-10 h-10 text-emerald-200" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-gray-500 text-sm">Total Earned</p>
                    <p className="text-xl font-bold text-gray-900">{formatCurrency(balance?.total_earnings || 0)}</p>
                  </div>
                  <IndianRupee className="w-8 h-8 text-gray-400" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-gray-500 text-sm">Total Withdrawn</p>
                    <p className="text-xl font-bold text-gray-900">{formatCurrency(balance?.total_withdrawn || 0)}</p>
                  </div>
                  <ArrowDownToLine className="w-8 h-8 text-gray-400" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-gray-500 text-sm">Pending</p>
                    <p className="text-xl font-bold text-yellow-600">{formatCurrency(balance?.pending_withdrawal || 0)}</p>
                  </div>
                  <Clock className="w-8 h-8 text-yellow-500" />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-4 mb-6">
            <Button 
              onClick={() => setShowWithdrawModal(true)}
              className="bg-emerald-600 hover:bg-emerald-700"
              disabled={(balance?.available_balance || 0) < 500}
            >
              <ArrowDownToLine className="w-4 h-4 mr-2" />
              Request Withdrawal
            </Button>
            <Button 
              variant="outline"
              onClick={() => setShowBankModal(true)}
            >
              <Building2 className="w-4 h-4 mr-2" />
              {bankDetails?.has_bank_details ? 'Update Bank Details' : 'Add Bank Details'}
            </Button>
          </div>

          {/* Bank Details Card */}
          {bankDetails?.has_bank_details && (
            <Card className="mb-6">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <CreditCard className="w-5 h-5 text-blue-600" />
                  Bank Account
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div>
                    <p className="text-gray-500">Account Holder</p>
                    <p className="font-medium">{bankDetails.bank_details?.account_holder}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Account Number</p>
                    <p className="font-medium">{bankDetails.bank_details?.account_number}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">IFSC Code</p>
                    <p className="font-medium">{bankDetails.bank_details?.ifsc_code}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Bank</p>
                    <p className="font-medium">{bankDetails.bank_details?.bank_name}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Withdrawal History */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <History className="w-5 h-5 text-purple-600" />
                Withdrawal History
              </CardTitle>
            </CardHeader>
            <CardContent>
              {history.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Wallet className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                  <p>No withdrawal requests yet</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {history.map((withdrawal, idx) => (
                    <div 
                      key={withdrawal.id || idx}
                      className="flex items-center justify-between p-4 bg-gray-50 rounded-lg"
                    >
                      <div className="flex items-center gap-4">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                          withdrawal.status === 'completed' ? 'bg-green-100' :
                          withdrawal.status === 'pending' ? 'bg-yellow-100' : 'bg-gray-100'
                        }`}>
                          {withdrawal.status === 'completed' ? (
                            <CheckCircle className="w-5 h-5 text-green-600" />
                          ) : withdrawal.status === 'pending' ? (
                            <Clock className="w-5 h-5 text-yellow-600" />
                          ) : (
                            <AlertCircle className="w-5 h-5 text-gray-600" />
                          )}
                        </div>
                        <div>
                          <p className="font-semibold">{formatCurrency(withdrawal.amount)}</p>
                          <p className="text-sm text-gray-500">
                            {new Date(withdrawal.requested_at).toLocaleDateString('en-IN', {
                              day: 'numeric',
                              month: 'short',
                              year: 'numeric'
                            })}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        {getStatusBadge(withdrawal.status)}
                        {withdrawal.transaction_ref && (
                          <p className="text-xs text-gray-500 mt-1">Ref: {withdrawal.transaction_ref}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Withdraw Modal */}
      {showWithdrawModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md">
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span>Request Withdrawal</span>
                <Button variant="ghost" size="sm" onClick={() => setShowWithdrawModal(false)}>
                  <X className="w-4 h-4" />
                </Button>
              </CardTitle>
              <CardDescription>
                Available balance: {formatCurrency(balance?.available_balance || 0)}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Amount (₹)</Label>
                <Input
                  type="number"
                  placeholder="Enter amount"
                  value={withdrawAmount}
                  onChange={(e) => setWithdrawAmount(e.target.value)}
                  min={500}
                  max={balance?.available_balance || 0}
                />
                <p className="text-xs text-gray-500 mt-1">Minimum: ₹500</p>
              </div>
              <div>
                <Label>Notes (Optional)</Label>
                <Input
                  placeholder="Any special instructions"
                  value={withdrawNotes}
                  onChange={(e) => setWithdrawNotes(e.target.value)}
                />
              </div>
              <div className="bg-blue-50 p-3 rounded-lg text-sm text-blue-800">
                <p>Processing time: {balance?.processing_days}</p>
                <p>Amount will be credited to your registered bank account</p>
              </div>
              <div className="flex gap-2 pt-4">
                <Button variant="outline" className="flex-1" onClick={() => setShowWithdrawModal(false)}>
                  Cancel
                </Button>
                <Button 
                  className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                  onClick={handleWithdraw}
                  disabled={submitting}
                >
                  {submitting ? 'Processing...' : 'Submit Request'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Bank Details Modal */}
      {showBankModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md">
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span>Bank Details</span>
                <Button variant="ghost" size="sm" onClick={() => setShowBankModal(false)}>
                  <X className="w-4 h-4" />
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Account Holder Name *</Label>
                <Input
                  placeholder="As per bank records"
                  value={bankForm.account_holder}
                  onChange={(e) => setBankForm({...bankForm, account_holder: e.target.value})}
                />
              </div>
              <div>
                <Label>Account Number *</Label>
                <Input
                  placeholder="Enter account number"
                  value={bankForm.account_number}
                  onChange={(e) => setBankForm({...bankForm, account_number: e.target.value})}
                />
              </div>
              <div>
                <Label>IFSC Code *</Label>
                <Input
                  placeholder="e.g., SBIN0001234"
                  value={bankForm.ifsc_code}
                  onChange={(e) => setBankForm({...bankForm, ifsc_code: e.target.value.toUpperCase()})}
                  maxLength={11}
                />
              </div>
              <div>
                <Label>Bank Name *</Label>
                <Input
                  placeholder="e.g., State Bank of India"
                  value={bankForm.bank_name}
                  onChange={(e) => setBankForm({...bankForm, bank_name: e.target.value})}
                />
              </div>
              <div>
                <Label>Branch</Label>
                <Input
                  placeholder="Branch name (optional)"
                  value={bankForm.branch}
                  onChange={(e) => setBankForm({...bankForm, branch: e.target.value})}
                />
              </div>
              <div className="flex gap-2 pt-4">
                <Button variant="outline" className="flex-1" onClick={() => setShowBankModal(false)}>
                  Cancel
                </Button>
                <Button 
                  className="flex-1 bg-blue-600 hover:bg-blue-700"
                  onClick={handleSaveBankDetails}
                  disabled={submitting}
                >
                  {submitting ? 'Saving...' : 'Save Details'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default ObserverWithdrawal;
